import type { DowntimeMode } from "../downtime/DowntimeMode";
import type { Quota } from "../quotas/QuotaRegistry";
import type { AuthServiceRestrictions } from "../auth/auth";
import type { UsageEntitlementStatus } from "../usage/UsageEntitlementStatus";
import type { UsageFeature } from "../usage/UsageFeature";
import type { PlanLimitMetric } from "../subscription/PlanLimits";

/** The downtime error type. */
export const DowntimeErrorType = "downtime";

/** The different types of errors thrown by GraphQL API endpoints. */
export type ClientErrorType =
  | "aborted"
  | "feature not accessible"
  | "invalid input"
  | "ratelimited"
  | "network error"
  | "authentication error"
  | "forbidden"
  | "bootstrap error"
  | "unknown"
  | "internal error"
  | "other"
  | "user error"
  | "graphql error"
  | "lock timeout"
  | "ip addr restricted"
  | "payload too large"
  | "invalid team access"
  | "usage limit exceeded"
  | typeof DowntimeErrorType;

/** Flags for client errors. */
export type ClientErrorFlags = {
  /** When set, indicates to the client that it is probably out of sync and should re-bootstrap itself. */
  reSyncRequired?: boolean;
  /**
   * When set, indicates to the client that it accessed a entity that has already been deleted. The client is safe
   * to ignore the failure and just archive its local copy of the model.
   */
  entityDeleted?: boolean;
  /**
   * When set, indicates that client made a bootstrap request for sync groups user does not have access to.
   * Client has then an option to decide on how to handle this situation.
   */
  noSyncGroupsAccess?: boolean;
  /**
   * When set, indicates that requested bootstrap cache is currently being generated and client should try again in
   * provided number of milliseconds. On second try, client should force bypassing the cache generation deduplication by
   * setting `forceBypassCacheDeduplication` query parameter.
   */
  generatingBootstrapCacheRetryIn?: number;
  /**
   * When set, indicates that the postindex schema of the client differs from the schema of the server, so the server
   * cannot write a `px` bootstrap snapshot for the client. The client then uses the JSON bootstrap.
   */
  postindexSchemaMismatch?: boolean;
};

/** Field errors. */
export type ClientErrorFieldErrors = {
  [field: string]: string[];
};

/** Options for an error. */
export type ClientErrorOptions = {
  /** Structured details for API consumers. */
  meta?: ClientErrorMetadata;
  /** Flags with instructions for the client. */
  flags?: ClientErrorFlags;
  /**
   * The field errors to include with the error. These can be used by the client to display what the error for each
   * of the erroneous fields was.
   */
  fieldErrors?: ClientErrorFieldErrors;
  /** Title for the client's error toast, replacing its generic default. */
  userPresentableTitle?: string;
};

/** Metadata that can help the client better handle the error or be more useful to the user. */
export type ClientErrorMetadata = {
  /** Better suggestions when the auth service is restricted. */
  authServiceRestriction?: AuthServiceRestrictions;
  /**
   * How widely a code host rate limit applies. `budget` covers one of Linear's per-user GitHub budgets: one host,
   * REST or GraphQL, reads or writes. `host` covers every request to that code host, as GitHub's own limits do.
   */
  codeHostRateLimitScope?: "budget" | "host";
  /** Extra info when rate limit exceeded errors happen. */
  rateLimitResult?: {
    allowed: boolean;
    requested: number;
    remaining: number;
    /** Duration in milliseconds. */
    duration: number;
  };
  /** Information about the downtime mode. */
  downtimeMode?: DowntimeMode;
  /** The IP address that was restricted. */
  ip?: string;
  /** The country code that was restricted. */
  countryCode?: string;
  /** The IDs of teams that the user doesn't have access to. */
  invalidTeamIds?: string[];
  /** Up to five requested IDs that could not be found or accessed. */
  missingIds?: string[];
  /** The quota that was exceeded. */
  quota?: Quota;
  /** The usage limit that was exceeded. */
  usageMetric?: PlanLimitMetric;
  /** The usage feature whose entitlement is currently blocking access. */
  usageFeature?: UsageFeature;
  /** The blocking entitlement status (e.g. exhausted, hard-capped). */
  usageEntitlementStatus?: UsageEntitlementStatus;
  /** Features with spendable credit when none of the remaining credit covers the blocked feature. */
  featuresWithSpendableCredit?: UsageFeature[];
};

/**
 * Error code for a database connection that died under a running transaction. Shared between the API error class and
 * the client's temporary-error classification, so both sides key on the same string.
 */
export const DATABASE_CONNECTION_LOST_ERROR_CODE = "DATABASE_CONNECTION_LOST";

/** Additional data added to the extension property of a ClientError returned by the GraphQL API. */
export type ClientErrorExtension = {
  /** The type of the error. */
  type: ClientErrorType;
  /** Whether this was a user error. */
  userError: boolean;
  /** A string that can be presented to the users as such. */
  userPresentableMessage?: string;
  /** Title for the client's error toast, replacing its generic default. */
  userPresentableTitle?: string;
  /** Flags associated with the error. */
  flags?: ClientErrorFlags;
  /** Field specific validation errors unless captured on schema level. */
  fieldErrors?: ClientErrorFieldErrors;
  /** An error code. */
  code?: string;
  /** The stack trace of the error. This is never sent to the client. */
  exception?: { stack?: string; warnOnError?: boolean };
  /** Metadata that can help client better handle the error. */
  meta?: ClientErrorMetadata;
  /** Populated only with "PAYLOAD_TOO_LARGE" error and represents the maximum request payload length. */
  limit?: number;
  /** Populated only with "PAYLOAD_TOO_LARGE" error and represents the current request payload length. */
  length?: number;
  /** Internally used on api side to set correct rest response status code. */
  statusCode?: number;
  /** Additional HTTP options to be sent to the client. Handled by Apollo directly. */
  http?: Record<string, unknown>;
};

/**
 * Checks whether an unknown value has the minimum shape of a GraphQL client error extension payload.
 *
 * @param extensions The value to check.
 * @returns `true` if the value looks like a {@link ClientErrorExtension}.
 */
export function isClientErrorExtensions(extensions: unknown): extensions is ClientErrorExtension {
  return (
    typeof extensions === "object" &&
    extensions !== null &&
    "type" in extensions &&
    typeof extensions.type === "string" &&
    "userError" in extensions &&
    typeof extensions.userError === "boolean"
  );
}
